import { useContext, useEffect, useMemo, useState } from 'react'
import { Box, CircularProgress, Stack, Typography } from '@mui/material'
import { useParams, useSearchParams } from 'react-router-dom'
import { AuthContext, IAuthContext } from 'react-oauth2-code-pkce'
import { com } from '@eidu/form'
import { EntityTypesContext } from '../../io/context/EntityTypes'
import useEntityById from '../../io/useEntityById'
import { fieldValueToString } from '../../domain/entity/DraftFieldValue'
import { findAssetTypeByLabel, type RenderData } from './assetTypes'
import AssetRenderer from './AssetRenderer'
import EntityNotAccessibleError from '../entity/EntityNotAccessibleError'
import EntityTypeNotAccessibleError from '../entity/EntityTypeNotAccessibleError'
import { getTypeDisplayName } from '../../util/entityType/typeNameSchema'
import { createAuthContext } from '../../api/authorization/AuthenticationContext'
import apiBaseUrl from '../../api/baseUrl'
import entityApiUrl from '../../api/entity/baseUrl'
import { AssetEntityTypeConfig, useAssetTypeRegistry } from './useAssetTypeRegistry'
import EntityWithLabelAndRelated from '../../domain/entity/EntityWithLabelAndRelated'
import { EMBED_MAX_DEPTH } from './shared/embedDepth'
import FieldType = com.eidu.sharedlib.entity.field.FieldType
import FieldId = com.eidu.sharedlib.entity.field.FieldId
import ReferenceValue = com.eidu.sharedlib.entity.field.ReferenceValue
import entityIdFromStringOrNull = com.eidu.sharedlib.entity.entityIdFromStringOrNull

const getAssetTypeLabel = (asset: EntityWithLabelAndRelated, typeFieldId?: FieldId) => {
  if (!typeFieldId) return undefined
  const values = asset.entity.valuesByFieldId.asJsReadonlyMapView()
  const typeValue = values.get(typeFieldId) ?? null
  if (typeValue instanceof ReferenceValue) {
    const referenced = asset.getReferencedEntity(typeValue.value)
    return referenced?.primaryLabelText?.trim()
  }
  return typeValue ? fieldValueToString(typeValue).trim() : undefined
}

const resolveAssetTypeForEntity = (asset: EntityWithLabelAndRelated, assetConfig?: AssetEntityTypeConfig) => {
  if (!assetConfig) return undefined
  if (assetConfig.allowedTypes.length === 1 && !assetConfig.typeFieldId) return assetConfig.allowedTypes[0]
  const typeLabel = getAssetTypeLabel(asset, assetConfig.typeFieldId)
  if (!typeLabel) return assetConfig.allowedTypes.length === 1 ? assetConfig.allowedTypes[0] : undefined
  const resolved = findAssetTypeByLabel(typeLabel)
  if (!resolved) return undefined
  return assetConfig.allowedTypeIds.includes(resolved.id) ? resolved : undefined
}

const resolveFieldTypeName = (fieldType: FieldType) => {
  if (typeof fieldType === 'string') return fieldType
  if (typeof fieldType === 'number') {
    return (FieldType as unknown as Record<number, string>)[fieldType] ?? String(fieldType)
  }
  return String(fieldType)
}

const parseEmbedDepth = (value: string | null) => {
  if (!value) return 1
  const parsed = Number.parseInt(value, 10)
  if (!Number.isFinite(parsed)) return 1
  if (parsed < 0) return 0
  return parsed
}

const parseInstanceId = (value: string | null) => {
  const trimmed = value?.trim()
  return trimmed ? trimmed : undefined
}

const EMBED_SPINNER_DELAY_MS = 1000
const EMBED_SPINNER_SIZE_PX = 28
const TERMINAL_ERROR_STABILIZATION_MS = 2000

const AssetEmbedRoute = () => {
  const params = useParams()
  const [searchParams] = useSearchParams()
  const assetId = params.assetId ? entityIdFromStringOrNull(params.assetId) ?? undefined : undefined
  const embedDepth = parseEmbedDepth(searchParams.get('depth'))
  const instanceId = parseInstanceId(searchParams.get('instance'))

  const entityTypesRepo = useContext(EntityTypesContext)
  const entityTypes = entityTypesRepo.useAll()
  const entityTypesLoading = entityTypesRepo.useLoading()

  const { entity, loading: entityLoading } = useEntityById(assetId)
  const iAuthContext = useContext<IAuthContext>(AuthContext)
  const authContext = useMemo(
    () => createAuthContext(iAuthContext),
    [iAuthContext.token, iAuthContext.idTokenData?.organization_id, iAuthContext.idTokenData?.user_entity_id]
  )

  const { assetEntityTypesById, loading: assetRegistryLoading } = useAssetTypeRegistry(entityTypes ?? undefined)

  const entityType = useMemo(() => {
    if (!entity || !entityTypes) return undefined
    return entityTypes.get(entity.entity.typeId) ?? undefined
  }, [entity, entityTypes])

  const assetConfig = useMemo(
    () => (entityType ? assetEntityTypesById.get(entityType.id) : undefined),
    [assetEntityTypesById, entityType]
  )

  const assetType = useMemo(
    () => (entity && assetConfig ? resolveAssetTypeForEntity(entity, assetConfig) : undefined),
    [assetConfig, entity]
  )

  const payload = useMemo(() => {
    if (!entity || !assetConfig) return ''
    const values = entity.entity.valuesByFieldId.asJsReadonlyMapView()
    return fieldValueToString(values.get(assetConfig.payloadFieldId) ?? null)
  }, [assetConfig, entity])

  const renderData = useMemo<RenderData | undefined>(() => {
    if (!entity || !entityType) return undefined
    const values = entity.entity.valuesByFieldId.asJsReadonlyMapView()
    const entitySnapshot = {
      id: entity.entity.id.toString(),
      typeId: entityType.id.toString(),
      typeName: getTypeDisplayName(entityType.name),
      fields: entityType.fields.asJsReadonlyArrayView().map((field) => ({
        id: field.id.toString(),
        name: field.name,
        type: resolveFieldTypeName(field.type),
        value: fieldValueToString(values.get(field.id) ?? null),
        required: field.required,
      })),
    }
    const auth =
      authContext &&
      ({
        accessToken: authContext.accessToken,
        organizationId: authContext.organizationId,
        apiBaseUrl,
        entityApiUrl,
        userEntityId: authContext.userEntityId,
      } as const)
    return { entity: entitySnapshot, auth: auth ?? undefined, context: { embedDepth } }
  }, [authContext, embedDepth, entity, entityType])

  const depthExceeded = embedDepth > EMBED_MAX_DEPTH
  const entityResolved = entity !== undefined || !assetId
  const entityTypesResolved = entityTypes !== undefined
  const isBootstrapping =
    !depthExceeded &&
    (!entityResolved || !entityTypesResolved || entityLoading || entityTypesLoading || assetRegistryLoading)
  const hasRenderableAsset = Boolean(entity && entityType && assetConfig && assetType)
  const hasTerminalResolutionError = !depthExceeded && !isBootstrapping && !hasRenderableAsset
  const [showBootSpinner, setShowBootSpinner] = useState(false)
  const [showTerminalError, setShowTerminalError] = useState(false)

  useEffect(() => {
    if (!isBootstrapping) {
      setShowBootSpinner(false)
      return () => undefined
    }
    const timeoutId = window.setTimeout(() => setShowBootSpinner(true), EMBED_SPINNER_DELAY_MS)
    return () => window.clearTimeout(timeoutId)
  }, [isBootstrapping])

  useEffect(() => {
    if (!hasTerminalResolutionError) {
      setShowTerminalError(false)
      return () => undefined
    }
    const timeoutId = window.setTimeout(() => setShowTerminalError(true), TERMINAL_ERROR_STABILIZATION_MS)
    return () => window.clearTimeout(timeoutId)
  }, [hasTerminalResolutionError])

  return (
    <>
      {depthExceeded && (
        <Stack sx={{ height: '100%', minHeight: 0, alignItems: 'center', justifyContent: 'center', p: 3 }}>
          <Typography variant="h6" gutterBottom>
            This asset is nested too deeply to render here.
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Please open the asset directly or simplify the dashboard.
          </Typography>
        </Stack>
      )}
      {!depthExceeded && isBootstrapping && (
        <Stack sx={{ height: '100%', minHeight: 0, alignItems: 'center', justifyContent: 'center', bgcolor: '#fff' }}>
          {showBootSpinner && <CircularProgress color="primary" size={EMBED_SPINNER_SIZE_PX} />}
        </Stack>
      )}
      {!depthExceeded && !isBootstrapping && hasTerminalResolutionError && !showTerminalError && (
        <Stack sx={{ height: '100%', minHeight: 0, alignItems: 'center', justifyContent: 'center', bgcolor: '#fff' }}>
          <CircularProgress color="primary" size={EMBED_SPINNER_SIZE_PX} />
        </Stack>
      )}
      {!depthExceeded && hasRenderableAsset && (
        <Box sx={{ height: '100%', minHeight: 0 }}>
          <AssetRenderer
            content={payload}
            assetType={assetType}
            renderData={renderData}
            layout="fill"
            showLog={false}
            instanceId={instanceId}
            forwardLogs
          />
        </Box>
      )}
      {!depthExceeded && hasTerminalResolutionError && showTerminalError && !entity && <EntityNotAccessibleError />}
      {!depthExceeded && hasTerminalResolutionError && showTerminalError && entity && !entityType && (
        <EntityTypeNotAccessibleError />
      )}
      {!depthExceeded &&
        hasTerminalResolutionError &&
        showTerminalError &&
        entity &&
        entityType &&
        (!assetConfig || !assetType) && <EntityNotAccessibleError />}
    </>
  )
}

export default AssetEmbedRoute
